import React, { useEffect, useState } from "react";
import Header from "../Components/Common/Header";
import { motion } from "framer-motion";
import "../Styles/Admin/Dashboard.css";
import StatCard from "../Components/Common/StatCard";
import UsersTable from "../Components/Admin/UserTable";
import { 
  BarChart2, 
  Users, 
  CalendarCheck, 
  LayoutDashboard,
  CheckCircle,
  XCircle,
  MapPin
} from "lucide-react";
import AttendenceOverviewChart from "../Components/Admin/AttendenceOverviewChart";
import CheckinChart from "../Components/Admin/CheckinChart";
import { API_BASE_URL } from "../apiConfig";
import Toast from "../Components/Common/Toast";

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalEmployees: 0,
    totalCheckIns: 0,
    onTimePercentage: "0.0",
  });
  const [location, setLocation] = useState(null);
  const [status, setStatus] = useState(localStorage.getItem("adminStatus") || "Not Checked In");
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ message: "", type: "" });

  useEffect(() => {


    const fetchStats = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/dashboard-stats`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("idToken")}`,
          },
        });
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          window.location.href = '/';
          return;
        }
        const data = await response.json();
        if (data.success) {
          setStats(data.stats);
        } else {
          console.error("Failed to fetch dashboard stats:", data.message);
        }
      } catch (err) {
        console.error("Error fetching dashboard stats:", err);
      }
    };

    fetchStats();
    checkCurrentStatus();
  }, []);

  const checkCurrentStatus = async () => {
    try {
      const token = localStorage.getItem("idToken");
      const response = await fetch(`${API_BASE_URL}/attendance/status`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.status === 401 || response.status === 403) {
        localStorage.clear();
        window.location.href = '/';
        return;
      }
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setStatus(data.status);
          localStorage.setItem("adminStatus", data.status);
        }
      }
    } catch (err) {
      console.error("Error checking status:", err);
    }
  };

  const getLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation not supported");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        };
        setLocation(coords);
      },
      () => alert("Unable to retrieve location")
    );
  };

  const handleCheckInOut = async (type) => {
    // Helper to perform check-in/out
    const doCheckInOut = async (coords) => {
      setLoading(true);
      const endpoint = type === "Check In" ? "/attendance/check-in" : "/attendance/check-out";
      try {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("idToken")}`,
          },
          body: JSON.stringify({
            latitude: coords.lat,
            longitude: coords.lng,
          }),
        });
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          window.location.href = '/';
          return;
        }
        const data = await response.json();
        if (data.success) {
          const newStatus = type === "Check In" ? "Checked In" : "Checked Out";
          setStatus(newStatus);
          localStorage.setItem("adminStatus", newStatus);
          // Refresh stats after check-in/out
          const statsResponse = await fetch(`${API_BASE_URL}/admin/dashboard-stats`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("idToken")}`,
            },
          });
          if (statsResponse.status === 401 || statsResponse.status === 403) {
            localStorage.clear();
            window.location.href = '/';
            return;
          }
          if (statsResponse.ok) {
            const statsData = await statsResponse.json();
            if (statsData.success) {
              setStats(statsData.stats);
            }
          }
        } else {
          setToast({ message: data.message || "Failed to process request.", type: "error" });
        }
      } catch (err) {
        console.error("Error during check-in/out:", err);
        setToast({ message: "An error occurred. Please try again.", type: "error" });
      } finally {
        setLoading(false);
      }
    };

    if (!location) {
      if (!navigator.geolocation) {
        alert("Geolocation not supported");
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const coords = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          };
          setLocation(coords);
          doCheckInOut(coords); 
        },
        () => alert("Unable to retrieve location")
      );
      return;
    }
    // If location is already available
    doCheckInOut(location);
  };

  return (
    <div className="dashboard-container">
      <div className="ap-wrap-content">
        <Header 
          title="Dashboard" 
          description={"Overview of attendance statistics"} 
          icon={<LayoutDashboard size={28} color="#f3f4f6" />} 
        />
        
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast({ message: "", type: "" })}
        />
        
        {/* Admin Attendance Card */}
        <div className="admin-attendance-card">
          <div className="admin-attendance-header">
            <h2>Your Attendance</h2>
            <div className="admin-attendance-status">
              <span className={`status-indicator ${status === "Checked In" ? "checked-in" : "checked-out"}`}></span>
              <span className="status-text">{status}</span>
            </div>
          </div>
          
          <div className="admin-attendance-location">
            {location ? (
              <div className="location-info">
                <MapPin size={16} />
                <span>Location captured</span>
              </div>
            ) : (
              <button className="location-button" onClick={getLocation}>
                <MapPin size={16} />
                <span>Get Location</span>
              </button>
            )}
          </div>
          
          <div className="admin-attendance-buttons">
            <button 
              className={`check-in-btn ${status === "Checked In" ? "disabled" : ""}`}
              onClick={() => handleCheckInOut("Check In")}
              disabled={status === "Checked In" || loading}
            >
              <CheckCircle size={18} />
              Check In
            </button>
            <button 
              className={`check-out-btn ${status === "Checked Out" || status === "Not Checked In" ? "disabled" : ""}`}
              onClick={() => handleCheckInOut("Check Out")}
              disabled={status === "Checked Out" || status === "Not Checked In" || loading}
            >
              <XCircle size={18} />
              Check Out
            </button>
          </div>
        </div>
        
        <main className="dashboard-main">
          {/* STATS */}
          <motion.div
            className="stats-grid"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            <StatCard
              name="Total Employees"
              icon={Users}
              value={stats.totalEmployees}
              color="#6366F1"
            />
            <StatCard
              name="Total Check-ins"
              icon={CalendarCheck}
              value={stats.totalCheckIns}
              color="#8B5CF6"
            />
            
            <StatCard
              name="On-Time Check-ins (%)"
              icon={BarChart2}
              value={`${stats.onTimePercentage}%`}
              color="#10B981"
            />
          </motion.div>

          <UsersTable />
          
          <div className="charts-grid">
            <AttendenceOverviewChart />
            <CheckinChart />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
